import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import {
  CARE_SNAPSHOT_SECTIONS,
  CARE_SNAPSHOT_SECTION_KEYS,
  CARE_SNAPSHOT_STEPS,
} from "../../../constants/careSnapshotSections";
import colors from "../../../assets/theme/colors";
import { CustomizableAutocomplete } from "../../UI";
import SupportGoalsSection from "./SupportGoalsSection";
import { createEmptyCareContext, normalizeCareContext } from "../../../utils/careContext";
import { buildCareProfileOverview } from "../../../utils/careProfileSections";
import { buildCareProfileStory } from "../../../utils/careProfileStory";
import { getChildProfileSetupStatus } from "../../../utils/profileCompletion";
import CareProfileOverview from "./CareProfileOverview";
import { NeedToKnowCard } from "./CareProfileOverviewCards";
import CareProfileSectionEditor from "./CareProfileSectionEditor";
import CareProfileStoryEditSheet from "./CareProfileStoryEditSheet";
import CareProfileStoryReview from "./CareProfileStoryReview";
import StructuredInformationSection from "./StructuredInformationSection";
import AllergiesFoodSection from "./AllergiesFoodSection";
import { getProfileLensOptions } from "./profileLensOptions";
import PersonIdentityFields from "./PersonIdentityFields";
import PersonAdministrativeFields from "./PersonAdministrativeFields";
import { deriveAgeGroupFromDateOfBirth, deriveAgeLabelFromDateOfBirth } from "../../../utils/profileAge";
import {
  buildDisplaySupportProfile,
  subscribeToKnowledgeFacts,
} from "../../../features/knowledgeItems";
import { buildProfileContractSafely } from "../../../features/profileContract";
import ProfileInboundEmailCard from "../../../features/inboundEmail/ProfileInboundEmailCard";

const fieldInputSx = {
  "& .MuiInputLabel-root": {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: colors.semantic.profileFlow.fieldLabel,
  },
  "& .MuiInputBase-root": {
    minHeight: 50,
    borderRadius: "12px",
    bgcolor: colors.semantic.profileFlow.fieldBg,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.semantic.profileFlow.cardBorder,
  },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.semantic.profileFlow.focusBorder,
    borderWidth: "1px",
  },
};

const ChildProfileFlowContent = ({
  mode = "add",
  stage = "intake",
  canEditProfile = true,
  canViewDocuments = false,
  openInboundEmailOnLoad = false,
  submitError,
  setSubmitError,
  documentError,
  setDocumentError,
  name,
  setName,
  age,
  setAge,
  dateOfBirth,
  setDateOfBirth,
  pronouns,
  setPronouns,
  language,
  setLanguage,
  setPhoto,
  photoURL,
  setPhotoURL,
  selectedConditions,
  setSelectedConditions,
  careContext,
  setCareContext,
  foodAllergies,
  setFoodAllergies,
  dietaryRestrictions,
  setDietaryRestrictions,
  sensoryIssues,
  setSensoryIssues,
  behavioralTriggers,
  setBehavioralTriggers,
  communicationNeeds,
  setCommunicationNeeds,
  supportProfile,
  setSupportProfile,
  child,
  childAccent,
  childId = "",
  renderDocumentDropZone,
  renderMedicationDetails,
  medicationDetails = [],
  providers = [],
  careTeamMemberCount = 0,
  activeProfileSectionId = "",
  activeProfileSectionLabel = "",
  currentStep = 0,
  landingView = null,
  onLandingViewChange,
  handbookChapterId = null,
  onHandbookChapterIdChange,
  handbookChapterQuickEntry = false,
  onHandbookChapterQuickEntryChange,
  onStepChange,
  onProfileSectionSelect,
  onOpenCareTeam,
  onOpenProviders,
  onOpenCollectedInformation,
  onOpenPlans,
  onProfileAddIntent,
  onCreate,
  onFinish,
  onSaveDraft,
  isSubmitting = false,
  uploadedDocuments = { medical: [], medications: [], behavioral: [] },
  childDisplayName = "",
}) => {
  const [activeStoryChapterId, setActiveStoryChapterId] = useState("");
  const [knowledgeFactState, setKnowledgeFactState] = useState({ childId: "", facts: [], status: "idle" });
  const safeUploadedDocuments = {
    medical: Array.isArray(uploadedDocuments?.medical) ? uploadedDocuments.medical : [],
    medications: Array.isArray(uploadedDocuments?.medications) ? uploadedDocuments.medications : [],
    behavioral: Array.isArray(uploadedDocuments?.behavioral) ? uploadedDocuments.behavioral : [],
  };
  const displayName = String(childDisplayName || name || "New profile").trim();
  const safeSupportProfile = useMemo(() => supportProfile || {}, [supportProfile]);
  const activeMedicationDetails = useMemo(() => (
    Array.isArray(medicationDetails)
      ? medicationDetails.filter((entry) => entry?.name && !entry.isArchived)
      : []
  ), [medicationDetails]);
  useEffect(() => {
    if (!childId) {
      setKnowledgeFactState({ childId: "", facts: [], status: "idle" });
      return undefined;
    }

    setKnowledgeFactState({ childId, facts: [], status: "loading" });
    return subscribeToKnowledgeFacts({
      childId,
      onFacts: (facts) => setKnowledgeFactState({ childId, facts, status: "ready" }),
      onError: () => setKnowledgeFactState({ childId, facts: [], status: "error" }),
    });
  }, [childId]);
  const displaySupportProfile = useMemo(() => buildDisplaySupportProfile({
    knowledgeFacts: knowledgeFactState.childId === childId ? knowledgeFactState.facts : [],
    legacySupportProfile: safeSupportProfile,
  }), [childId, knowledgeFactState, safeSupportProfile]);
  const safeCareContext = normalizeCareContext({
    ...createEmptyCareContext(),
    ...(careContext || {}),
    careFocus: selectedConditions,
  });
  const lensOptions = getProfileLensOptions(selectedConditions, safeCareContext);
  const profileContractResult = useMemo(() => buildProfileContractSafely({
    child: {
      ...(child || {}),
      conditions: selectedConditions,
      careContext: safeCareContext,
      medicalProfile: {
        ...(child?.medicalProfile || {}),
        communicationNeeds,
        foodAllergies,
        dietaryRestrictions,
        medicationRecords: activeMedicationDetails,
      },
      supportProfile: safeSupportProfile,
    },
    knowledgeFacts: knowledgeFactState.childId === childId ? knowledgeFactState.facts : [],
    careContext: safeCareContext,
    ageGroup: safeCareContext.ageGroup,
  }), [
    child,
    childId,
    communicationNeeds,
    foodAllergies,
    dietaryRestrictions,
    activeMedicationDetails,
    knowledgeFactState,
    safeCareContext,
    safeSupportProfile,
    selectedConditions,
  ]);
  const profileContract = profileContractResult.contract;
  const communicationChapterStatus = profileContractResult.error
    ? "error"
    : knowledgeFactState.status;
  const whatHelpsChapterStatus = profileContractResult.error
    ? "error"
    : knowledgeFactState.status;
  const watchForChapterStatus = profileContractResult.error
    ? "error"
    : knowledgeFactState.status;
  const dailyLifeChapterStatus = profileContractResult.error
    ? "error"
    : knowledgeFactState.status;
  const safetyChapterStatus = profileContractResult.error
    ? "error"
    : knowledgeFactState.status;
  const healthCareChapterStatus = profileContractResult.error
    ? "error"
    : knowledgeFactState.status;
  const goalsChapterStatus = profileContractResult.error
    ? "error"
    : "ready";
  const handleDateOfBirthChange = (value) => {
    setDateOfBirth?.(value);
    const nextAge = deriveAgeLabelFromDateOfBirth(value);
    const nextAgeGroup = deriveAgeGroupFromDateOfBirth(value);
    setAge?.(nextAge);
    if (nextAgeGroup) {
      updateCareContext("ageGroup", nextAgeGroup);
    }
  };
  const updateCareContext = (field, value) => {
    setCareContext?.((current) => normalizeCareContext({
      ...(current || {}),
      [field]: value,
      careFocus: selectedConditions,
    }));
  };

  const sectionVisuals = {
    [CARE_SNAPSHOT_SECTION_KEYS.ABOUT]: {
      icon: DescriptionOutlinedIcon,
      iconBg: colors.semantic.profileFlow.basicsIconBg,
      iconInk: colors.semantic.profileFlow.basicsIconInk,
    },
    [CARE_SNAPSHOT_SECTION_KEYS.DAILY_SUPPORT]: {
      icon: FavoriteBorderRoundedIcon,
      iconBg: colors.semantic.profileFlow.doneBg,
      iconInk: colors.semantic.profileFlow.doneText,
    },
    [CARE_SNAPSHOT_SECTION_KEYS.PATTERNS_RESPONSES]: {
      icon: AutoAwesomeOutlinedIcon,
      iconBg: colors.semantic.profileFlow.behaviorIconBg,
      iconInk: colors.semantic.profileFlow.behaviorIconInk,
    },
    [CARE_SNAPSHOT_SECTION_KEYS.GOALS_GROWTH]: {
      icon: FlagOutlinedIcon,
      iconBg: colors.semantic.profileFlow.supportIconBg || colors.semantic.profileFlow.reviewIconBg || colors.semantic.profileFlow.behaviorIconBg,
      iconInk: colors.semantic.profileFlow.supportIconInk || colors.semantic.profileFlow.completionText,
    },
    [CARE_SNAPSHOT_SECTION_KEYS.HEALTH_CARE]: {
      icon: MedicationOutlinedIcon,
      iconBg: colors.semantic.profileFlow.healthIconBg,
      iconInk: colors.semantic.profileFlow.healthIconInk,
    },
  };

  const hasProfileValue = (value) => {
    if (Array.isArray(value)) return value.some((item) => hasProfileValue(item));
    if (value && typeof value === "object") return Object.values(value).some((item) => hasProfileValue(item));
    return String(value || "").trim().length > 0;
  };

  const hasSectionDetails = (sectionKey) => {
    if (sectionKey === CARE_SNAPSHOT_SECTION_KEYS.ABOUT) {
      return hasProfileValue([
        name,
        age,
        photoURL,
        selectedConditions,
        safeSupportProfile.about,
        safeSupportProfile.emergencyContacts,
      ]);
    }

    if (sectionKey === CARE_SNAPSHOT_SECTION_KEYS.DAILY_SUPPORT) {
      return hasProfileValue([
        safeSupportProfile.whoTheyAre,
        safeSupportProfile.whatHelps,
        safeSupportProfile.communicationStyle,
        safeSupportProfile.communicationNotes,
        communicationNeeds,
      ]);
    }

    if (sectionKey === CARE_SNAPSHOT_SECTION_KEYS.PATTERNS_RESPONSES) {
      return hasProfileValue([
        sensoryIssues,
        behavioralTriggers,
        safeSupportProfile.whatCanBeDifficult,
        safeSupportProfile.earlyWarningSigns,
        safeSupportProfile.thingsThatUsuallyHelp,
        safeSupportProfile.thingsToAvoid,
        safeSupportProfile.safetyConsiderations,
        safeUploadedDocuments.behavioral,
      ]);
    }

    if (sectionKey === CARE_SNAPSHOT_SECTION_KEYS.GOALS_GROWTH) {
      return hasProfileValue([
        safeSupportProfile.careGoals,
        safeSupportProfile.familySupportTips,
        safeSupportProfile.schoolTherapySuggestions,
      ]);
    }

    if (sectionKey === CARE_SNAPSHOT_SECTION_KEYS.HEALTH_CARE) {
      return hasProfileValue([
        foodAllergies,
        dietaryRestrictions,
        activeMedicationDetails,
        safeUploadedDocuments.medical,
        safeUploadedDocuments.medications,
      ]);
    }

    return false;
  };

  const editSections = CARE_SNAPSHOT_SECTIONS.map((section) => ({
    ...section,
    ...sectionVisuals[section.key],
    statusLabel: hasSectionDetails(section.key) ? "Added" : "Add details",
  }));
  const profileOverview = buildCareProfileOverview({
    name,
    age,
    photoURL,
    selectedConditions,
    careContext: safeCareContext,
    careTeamMemberCount,
    medicalProfile: {
      foodAllergies,
      dietaryRestrictions,
      sensoryIssues,
      behavioralTriggers,
      communicationNeeds,
      medicationRecords: activeMedicationDetails,
    },
    supportProfile: displaySupportProfile,
    includeCareTeamSection: Boolean(onOpenCareTeam),
  });
  const profileSetupSource = {
    name,
    age,
    dateOfBirth,
    photoURL,
    selectedConditions,
    careContext: safeCareContext,
    medicalProfile: {
      foodAllergies,
      dietaryRestrictions,
      sensoryIssues,
      behavioralTriggers,
      communicationNeeds,
      medicationRecords: activeMedicationDetails,
    },
    supportProfile: safeSupportProfile,
  };
  const profileSetupOverview = buildCareProfileOverview({
    name,
    age,
    photoURL,
    selectedConditions,
    careContext: safeCareContext,
    careTeamMemberCount,
    medicalProfile: profileSetupSource.medicalProfile,
    supportProfile: safeSupportProfile,
    includeCareTeamSection: Boolean(onOpenCareTeam),
  });
  const profileSetupStatus = getChildProfileSetupStatus(profileSetupSource, {
    careTeamMemberCount,
    profileOverview: profileSetupOverview,
  });
  const profileStory = buildCareProfileStory({
    displayName,
    age,
    photoURL,
    selectedConditions,
    careContext: safeCareContext,
    medicalProfile: {
      foodAllergies,
      dietaryRestrictions,
      sensoryIssues,
      behavioralTriggers,
      communicationNeeds,
      medicationRecords: activeMedicationDetails,
    },
    supportProfile: displaySupportProfile,
    uploadedDocuments: safeUploadedDocuments,
    profileOverview,
    profileContract,
    knowledgeFacts: knowledgeFactState.childId === childId ? knowledgeFactState.facts : [],
  });
  const activeStoryChapter = profileStory.chapters.find((chapter) => chapter.id === activeStoryChapterId) || null;

  const renderMedicationContextNudge = ({ compact = false } = {}) => {
    const hasActiveMedications = activeMedicationDetails.length > 0;

    return (
      <Box
        sx={{
          p: compact ? 1.2 : 1.45,
          borderRadius: 2.2,
          border: `1px solid ${colors.semantic.profileFlow.healthIconBg}`,
          bgcolor: alpha(colors.semantic.profileFlow.healthIconBg, 0.35),
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr) auto",
          gap: 1.1,
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: compact ? 38 : 44,
            height: compact ? 38 : 44,
            borderRadius: 1.8,
            bgcolor: colors.semantic.profileFlow.healthIconBg,
            color: colors.semantic.profileFlow.healthIconInk,
            display: "grid",
            placeItems: "center",
          }}
        >
          <MedicationOutlinedIcon sx={{ fontSize: compact ? 18 : 21 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 500, color: colors.landing.heroText, fontSize: compact ? "0.9rem" : "0.98rem" }}>
            {hasActiveMedications ? `${activeMedicationDetails.length} active medication${activeMedicationDetails.length === 1 ? "" : "s"}` : "Medication details help future notes"}
          </Typography>
          <Typography sx={{ mt: 0.15, fontSize: "0.78rem", color: colors.semantic.ink2, lineHeight: 1.35 }}>
            {hasActiveMedications
              ? "These details help Lifelog recognize routine doses, changes, and medication-related notes."
              : "Add names now. Dose and schedule can come later, and these details can help connect notes about sleep, appetite, mood, or behavior."}
          </Typography>
        </Box>
        {!compact ? (
          <Button
            variant="outlined"
            onClick={() => onStepChange?.(CARE_SNAPSHOT_STEPS.HEALTH_CARE)}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 500,
              color: colors.semantic.profileFlow.healthIconInk,
              borderColor: colors.semantic.profileFlow.healthIconBg,
              whiteSpace: "nowrap",
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            {hasActiveMedications ? "Review meds" : "Add meds"}
          </Button>
        ) : null}
      </Box>
    );
  };

  const renderAlerts = () => {
    if (!submitError && !documentError) return null;

    return (
      <Stack spacing={1}>
        {submitError ? (
          <Alert severity="error" onClose={() => setSubmitError("")}>
            {submitError}
          </Alert>
        ) : null}
        {documentError ? (
          <Alert severity="warning" onClose={() => setDocumentError("")}>
            {documentError}
          </Alert>
        ) : null}
      </Stack>
    );
  };

  const renderReviewActions = () => {
    if (!canEditProfile || typeof onFinish !== "function") {
      return null;
    }

    return (
      <Stack spacing={1} sx={{ pt: 0.5 }}>
        <Button
          variant="contained"
          onClick={() => onFinish?.()}
          disabled={isSubmitting}
          fullWidth
          size="large"
          sx={{
            minHeight: 52,
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 500,
            bgcolor: colors.semantic.profileFlow.completionText,
            "&:hover": { bgcolor: colors.brand.navy },
          }}
        >
          {mode === "add" ? "Looks good — save profile" : "Looks good — save changes"}
        </Button>
      </Stack>
    );
  };

  const renderDashboard = () => (
    <Stack spacing={1.2}>
      {renderAlerts()}
      <ProfileInboundEmailCard
        childId={childId}
        personName={displayName}
        canManage={canEditProfile && mode === "edit"}
        openOnLoad={openInboundEmailOnLoad}
      />
      <CareProfileOverview
        child={child}
        childAccent={childAccent}
        profileName={name}
        displayName={displayName}
        age={age}
        photoURL={photoURL}
        selectedConditions={selectedConditions}
        careContext={safeCareContext}
        profileOverview={profileOverview}
        profileStory={profileStory}
        communicationChapter={profileContract.communication}
        communicationChapterStatus={communicationChapterStatus}
        knowledgeFacts={knowledgeFactState.childId === childId ? knowledgeFactState.facts : []}
        whatHelpsChapter={profileContract.whatHelpsComfort}
        whatHelpsChapterStatus={whatHelpsChapterStatus}
        watchForChapter={profileContract.watchFor}
        watchForChapterStatus={watchForChapterStatus}
        dailyLifeChapter={profileContract.dailyLifeRoutines}
        dailyLifeChapterStatus={dailyLifeChapterStatus}
        safetyChapter={profileContract.safety}
        safetyChapterStatus={safetyChapterStatus}
        healthCareChapter={profileContract.healthCare}
        healthCareChapterStatus={healthCareChapterStatus}
        goalsChapter={profileContract.goals}
        goalsChapterStatus={goalsChapterStatus}
        medications={activeMedicationDetails}
        providers={providers}
        foodAllergies={foodAllergies}
        dietaryRestrictions={dietaryRestrictions}
        documents={safeUploadedDocuments}
        stage={stage}
        mode={mode}
        isSubmitting={isSubmitting}
        onSectionSelect={onProfileSectionSelect}
        onStepChange={onStepChange}
        onCreate={onCreate}
        onFinish={onFinish}
        onOpenCareTeam={onOpenCareTeam}
        onOpenProviders={onOpenProviders}
        onOpenCollectedInformation={onOpenCollectedInformation}
        onOpenPlans={onOpenPlans}
        onAddIntent={onProfileAddIntent}
        canEditProfile={canEditProfile}
        canViewDocuments={canViewDocuments}
        profileSetupStatus={profileSetupStatus}
        landingView={landingView}
        onLandingViewChange={onLandingViewChange}
        handbookChapterId={handbookChapterId}
        onHandbookChapterIdChange={onHandbookChapterIdChange}
        handbookChapterQuickEntry={handbookChapterQuickEntry}
        onHandbookChapterQuickEntryChange={onHandbookChapterQuickEntryChange}
      />
    </Stack>
  );

  const renderProfileSectionEditor = () => (
    <Stack spacing={1.2}>
      {renderAlerts()}
      <CareProfileSectionEditor
        sectionId={activeProfileSectionId}
        sectionLabel={activeProfileSectionLabel}
        displayName={displayName}
        name={name}
        setName={setName}
        age={age}
        setAge={setAge}
        dateOfBirth={dateOfBirth}
        setDateOfBirth={handleDateOfBirthChange}
        pronouns={pronouns}
        setPronouns={setPronouns}
        language={language}
        setLanguage={setLanguage}
        setPhoto={setPhoto}
        photoURL={photoURL}
        setPhotoURL={setPhotoURL}
        selectedConditions={selectedConditions}
        setSelectedConditions={setSelectedConditions}
        careContext={safeCareContext}
        setCareContext={setCareContext}
        child={child}
        foodAllergies={foodAllergies}
        setFoodAllergies={setFoodAllergies}
        dietaryRestrictions={dietaryRestrictions}
        setDietaryRestrictions={setDietaryRestrictions}
        sensoryIssues={sensoryIssues}
        setSensoryIssues={setSensoryIssues}
        behavioralTriggers={behavioralTriggers}
        setBehavioralTriggers={setBehavioralTriggers}
        communicationNeeds={communicationNeeds}
        setCommunicationNeeds={setCommunicationNeeds}
        supportProfile={safeSupportProfile}
        setSupportProfile={setSupportProfile}
        renderMedicationDetails={renderMedicationDetails}
        renderDocumentDropZone={renderDocumentDropZone}
      />
    </Stack>
  );

  const renderSectionHero = (sectionKey) => {
    const section = editSections.find((item) => item.key === sectionKey);
    if (!section) return null;
    const SectionIcon = section.icon;

    return (
      <Box
        sx={{
          p: 1.4,
          borderRadius: 2.2,
          border: `1px solid ${section.iconBg}`,
          bgcolor: alpha(section.iconBg, 0.55),
          display: "flex",
          alignItems: "center",
          gap: 1.1,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.8,
            bgcolor: section.iconBg,
            color: section.iconInk,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <SectionIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 500, color: colors.landing.heroText }}>
            {section.name}
          </Typography>
          <Typography sx={{ mt: 0.1, fontSize: "0.8rem", color: colors.semantic.ink2 }}>
            {section.description}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderBasicsSection = () => (
    <Stack spacing={1.2}>
      {renderSectionHero(CARE_SNAPSHOT_SECTION_KEYS.ABOUT)}
      {renderAlerts()}
      <Box sx={{ p: 1.4, borderRadius: 2.2, border: `1px solid ${colors.semantic.profileFlow.cardBorder}`, bgcolor: colors.semantic.profileFlow.cardBg }}>
        <Stack spacing={1.35}>
          <PersonIdentityFields
            name={name}
            setName={setName}
            dateOfBirth={dateOfBirth}
            onDateOfBirthChange={handleDateOfBirthChange}
            fallbackAgeGroup={safeCareContext.ageGroup}
            pronouns={pronouns}
            setPronouns={setPronouns}
            language={language}
            setLanguage={setLanguage}
            setPhoto={setPhoto}
            photoURL={photoURL}
            setPhotoURL={setPhotoURL}
            selectedConditions={selectedConditions}
            setSelectedConditions={setSelectedConditions}
            fieldSx={fieldInputSx}
          />
          <PersonAdministrativeFields
            careContext={safeCareContext}
            suggestedCareSettings={lensOptions.careSetting}
            onCareSettingChange={(careSetting) => updateCareContext("careSetting", careSetting)}
            emergencyContacts={safeSupportProfile.emergencyContacts}
            onEmergencyContactsChange={(emergencyContacts) => setSupportProfile?.((current) => ({
              ...(current || {}),
              emergencyContacts,
            }))}
            child={child}
            fieldSx={fieldInputSx}
          />
        </Stack>
      </Box>
    </Stack>
  );

  const renderDailySupportSection = () => (
    <Stack spacing={1.2}>
      {renderSectionHero(CARE_SNAPSHOT_SECTION_KEYS.DAILY_SUPPORT)}
      {renderAlerts()}
      <SupportGoalsSection
        supportProfile={safeSupportProfile}
        setSupportProfile={setSupportProfile}
        childDisplayName={displayName}
        sectionGroup="daily"
        showIntro={false}
        supportOptions={lensOptions.supportProfile}
      />
      <Box sx={{ p: 1.4, borderRadius: 2.2, border: `1px solid ${colors.semantic.profileFlow.cardBorder}`, bgcolor: colors.semantic.profileFlow.cardBg }}>
        <Stack spacing={1.1}>
          <CustomizableAutocomplete
            options={lensOptions.communication}
            value={communicationNeeds}
            onChange={(event, newValue) => setCommunicationNeeds(newValue)}
            label="Communication support"
            addText="Add support"
            helperText="Practical communication supports for daily routines."
          />
        </Stack>
      </Box>
    </Stack>
  );

  const renderPatternsSection = () => (
    <Stack spacing={1.2}>
      {renderSectionHero(CARE_SNAPSHOT_SECTION_KEYS.PATTERNS_RESPONSES)}
      {renderAlerts()}
      <SupportGoalsSection
        supportProfile={safeSupportProfile}
        setSupportProfile={setSupportProfile}
        childDisplayName={displayName}
        sectionGroup="patterns"
        showIntro={false}
        supportOptions={lensOptions.supportProfile}
        sensoryIssues={sensoryIssues}
        onSensoryIssuesChange={(event, newValue) => setSensoryIssues(newValue)}
        sensoryOptions={lensOptions.sensory}
        behavioralTriggers={behavioralTriggers}
        onBehavioralTriggersChange={(event, newValue) => setBehavioralTriggers(newValue)}
        triggerOptions={lensOptions.triggers}
      />
    </Stack>
  );

  const renderGoalsSection = () => (
    <Stack spacing={1.2}>
      {renderSectionHero(CARE_SNAPSHOT_SECTION_KEYS.GOALS_GROWTH)}
      {renderAlerts()}
      <SupportGoalsSection
        supportProfile={safeSupportProfile}
        setSupportProfile={setSupportProfile}
        childDisplayName={displayName}
        sectionGroup="goals"
        showIntro={false}
        supportOptions={lensOptions.supportProfile}
      />
    </Stack>
  );

  const renderHealthCareSection = () => (
    <Stack spacing={1.2}>
      {renderSectionHero(CARE_SNAPSHOT_SECTION_KEYS.HEALTH_CARE)}
      {renderAlerts()}
      <Box sx={{ p: 1.4, borderRadius: 2.2, border: `1px solid ${colors.semantic.profileFlow.cardBorder}`, bgcolor: colors.semantic.profileFlow.cardBg }}>
        <Stack spacing={1.1}>
          {renderMedicationContextNudge({ compact: true })}
          {renderMedicationDetails?.()}
        </Stack>
      </Box>
      <AllergiesFoodSection
        childName={displayName}
        foodAllergies={foodAllergies}
        setFoodAllergies={setFoodAllergies}
        dietaryRestrictions={dietaryRestrictions}
        setDietaryRestrictions={setDietaryRestrictions}
        renderDocumentDropZone={renderDocumentDropZone}
      />
    </Stack>
  );

  const renderReview = () => {
    const handleStoryEditDone = async () => {
      try {
        await onSaveDraft?.();
        setActiveStoryChapterId("");
      } catch (error) {
        setSubmitError?.(error?.message || "Could not save this profile right now.");
      }
    };

    return (
      <>
        <Stack spacing={1.5}>
          <CareProfileStoryReview
            story={profileStory}
            canEditProfile={canEditProfile}
            onEditChapter={canEditProfile ? (chapter) => setActiveStoryChapterId(chapter?.id || "") : undefined}
          />
          <StructuredInformationSection
            childId={childId}
            displayName={displayName}
          />
          {renderReviewActions()}
        </Stack>
        {canEditProfile ? (
          <CareProfileStoryEditSheet
            open={Boolean(activeStoryChapter)}
            chapter={activeStoryChapter}
            onClose={() => setActiveStoryChapterId("")}
            displayName={displayName}
            name={name}
            setName={setName}
            age={age}
            setAge={setAge}
            dateOfBirth={dateOfBirth}
            setDateOfBirth={handleDateOfBirthChange}
            setPhoto={setPhoto}
            photoURL={photoURL}
            setPhotoURL={setPhotoURL}
            selectedConditions={selectedConditions}
            setSelectedConditions={setSelectedConditions}
            careContext={safeCareContext}
            onCareContextChange={updateCareContext}
            foodAllergies={foodAllergies}
            setFoodAllergies={setFoodAllergies}
            dietaryRestrictions={dietaryRestrictions}
            setDietaryRestrictions={setDietaryRestrictions}
            sensoryIssues={sensoryIssues}
            setSensoryIssues={setSensoryIssues}
            behavioralTriggers={behavioralTriggers}
            setBehavioralTriggers={setBehavioralTriggers}
            communicationNeeds={communicationNeeds}
            setCommunicationNeeds={setCommunicationNeeds}
            supportProfile={safeSupportProfile}
            setSupportProfile={setSupportProfile}
            renderMedicationDetails={renderMedicationDetails}
            renderDocumentDropZone={renderDocumentDropZone}
            onDone={handleStoryEditDone}
            isSaving={isSubmitting}
          />
        ) : null}
      </>
    );
  };

  const renderReadOnlyStory = () => (
    <Stack spacing={1.5}>
      <NeedToKnowCard items={profileOverview.needToKnowItems} />
      <CareProfileStoryReview
        story={profileStory}
        canEditProfile={false}
      />
      <StructuredInformationSection
        childId={childId}
        displayName={displayName}
      />
    </Stack>
  );

  if (!canEditProfile) {
    return renderReadOnlyStory();
  }

  if (activeProfileSectionId && currentStep > 0 && currentStep < CARE_SNAPSHOT_STEPS.REVIEW) {
    return renderProfileSectionEditor();
  }
  if (currentStep === CARE_SNAPSHOT_STEPS.ABOUT) return renderBasicsSection();
  if (currentStep === CARE_SNAPSHOT_STEPS.DAILY_SUPPORT) return renderDailySupportSection();
  if (currentStep === CARE_SNAPSHOT_STEPS.PATTERNS_RESPONSES) return renderPatternsSection();
  if (currentStep === CARE_SNAPSHOT_STEPS.GOALS_GROWTH) return renderGoalsSection();
  if (currentStep === CARE_SNAPSHOT_STEPS.HEALTH_CARE) return renderHealthCareSection();
  if (currentStep === CARE_SNAPSHOT_STEPS.REVIEW) return renderReview();
  return renderDashboard();
};

export default ChildProfileFlowContent;
